import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Copy, Check, ExternalLink, Code, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'sonner';

export default function WidgetInstallation() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyCode = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(id);
      toast.success('Code copied!');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const CodeBlock = ({ code, id, language = 'html' }: { code: string; id: string; language?: string }) => (
    <div className="relative">
      <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap break-all font-mono">
        <code className={`language-${language}`}>{code}</code>
      </pre>
      <Button
        size="sm"
        variant="secondary"
        className="absolute top-2 right-2"
        onClick={() => copyCode(code, id)}
      >
        {copiedCode === id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/settings/widget">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="font-heading font-bold text-2xl">Widget Installation</h1>
              <p className="text-sm text-muted-foreground">Step-by-step guides for popular platforms</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Quick Start */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Quick Start
            </CardTitle>
            <CardDescription>
              The simplest way to add the widget to any website
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Copy your embed code from the <Link to="/dashboard/settings/widget" className="text-primary hover:underline">Widget Settings</Link> page,
              then paste it where you want the widget to appear on your site.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Tip:</strong> The script tag method (recommended) automatically handles responsive sizing and works on any platform.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Platform Guides */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Platform-Specific Guides
            </CardTitle>
            <CardDescription>
              Detailed instructions for popular website builders
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="wordpress" className="w-full">
              <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5">
                <TabsTrigger value="wordpress">WordPress</TabsTrigger>
                <TabsTrigger value="squarespace">Squarespace</TabsTrigger>
                <TabsTrigger value="wix">Wix</TabsTrigger>
                <TabsTrigger value="shopify" className="hidden lg:flex">Shopify</TabsTrigger>
                <TabsTrigger value="html" className="hidden lg:flex">HTML</TabsTrigger>
              </TabsList>

              {/* WordPress */}
              <TabsContent value="wordpress" className="space-y-6 mt-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">WordPress Installation</h3>
                  <Badge variant="outline">Works with any theme</Badge>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">Option 1: Using a Custom HTML Block (Gutenberg)</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Edit the page where you want to add the widget</li>
                        <li>Click the <strong>+</strong> button to add a new block</li>
                        <li>Search for <strong>"Custom HTML"</strong> and select it</li>
                        <li>Paste your embed code into the block</li>
                        <li>Click <strong>Preview</strong> to see the widget</li>
                        <li>Save/Update the page</li>
                      </ol>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium">Option 2: Using the Classic Editor</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Edit the page in Classic Editor</li>
                        <li>Switch to the <strong>Text</strong> tab (not Visual)</li>
                        <li>Paste your embed code where you want the widget</li>
                        <li>Save/Update the page</li>
                      </ol>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium">Option 3: Using a Plugin</h4>
                      <p className="text-sm text-muted-foreground">
                        Install a plugin like <strong>"Insert Headers and Footers"</strong> or <strong>"WPCode"</strong> to add
                        the widget script to specific pages or site-wide.
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Squarespace */}
              <TabsContent value="squarespace" className="space-y-6 mt-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Squarespace Installation</h3>
                  <Badge variant="outline">Version 7.0 & 7.1</Badge>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">Using a Code Block</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>In the Squarespace editor, navigate to the page you want to add the widget to</li>
                        <li>Click <strong>Edit</strong> on the page</li>
                        <li>Click <strong>Add Block</strong> or the <strong>+</strong> button</li>
                        <li>Select <strong>Code</strong> from the block menu</li>
                        <li>In the code block settings, paste your embed code</li>
                        <li>Make sure <strong>"Display Source"</strong> is OFF</li>
                        <li>Save and publish your changes</li>
                      </ol>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Note:</strong> Code blocks require a Squarespace Business plan or higher.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium">Alternative: Code Injection</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Go to <strong>Settings → Advanced → Code Injection</strong></li>
                        <li>Add the script to the <strong>Footer</strong> section</li>
                        <li>Add a div container where you want the widget: <code className="bg-muted px-1 rounded">&lt;div id="widget-container"&gt;&lt;/div&gt;</code></li>
                        <li>Use the <code className="bg-muted px-1 rounded">data-container</code> attribute on the script</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Wix */}
              <TabsContent value="wix" className="space-y-6 mt-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Wix Installation</h3>
                  <Badge variant="outline">Wix Editor & Editor X</Badge>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">Using the HTML iFrame Element</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Open your site in the Wix Editor</li>
                        <li>Click <strong>Add Elements</strong> (+ button)</li>
                        <li>Go to <strong>Embed Code → Popular Embeds → Embed HTML</strong></li>
                        <li>Drag the element to your page</li>
                        <li>Click <strong>Enter Code</strong></li>
                        <li>Select <strong>"Code"</strong> tab</li>
                        <li>Paste your embed code (use the iframe version for Wix)</li>
                        <li>Click <strong>Update</strong></li>
                        <li>Resize the element as needed</li>
                        <li>Publish your site</li>
                      </ol>
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Important:</strong> For Wix, we recommend using the <strong>iframe</strong> embed method
                        instead of the script tag due to Wix's sandbox restrictions.
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Shopify */}
              <TabsContent value="shopify" className="space-y-6 mt-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Shopify Installation</h3>
                  <Badge variant="outline">Online Store 2.0</Badge>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">Using the Theme Editor</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Go to <strong>Online Store → Themes</strong></li>
                        <li>Click <strong>Customize</strong> on your current theme</li>
                        <li>Navigate to the page where you want the widget</li>
                        <li>Click <strong>Add section</strong></li>
                        <li>Select <strong>Custom Liquid</strong> or <strong>Custom HTML</strong></li>
                        <li>Paste your embed code</li>
                        <li>Save your changes</li>
                      </ol>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium">Using Theme Code</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Go to <strong>Online Store → Themes</strong></li>
                        <li>Click <strong>... → Edit code</strong></li>
                        <li>Find the template file for your page (e.g., <code className="bg-muted px-1 rounded">page.liquid</code>)</li>
                        <li>Add your embed code where you want the widget</li>
                        <li>Save the file</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Plain HTML */}
              <TabsContent value="html" className="space-y-6 mt-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Plain HTML Installation</h3>
                  <Badge variant="outline">Any website</Badge>

                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Simply paste the script tag anywhere in your HTML where you want the widget to appear.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium">Basic Example</h4>
                      <CodeBlock
                        id="html-basic"
                        code={`<!DOCTYPE html>
<html>
<head>
  <title>My Page</title>
</head>
<body>
  <h1>Get Your Renovation Preview</h1>

  <!-- Widget will appear here -->
  <script src="https://app.renovationai.com/widget/your-slug.js"></script>

</body>
</html>`}
                      />
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium">With Custom Container</h4>
                      <CodeBlock
                        id="html-container"
                        code={`<!-- Create a container div -->
<div id="my-widget-container"></div>

<!-- Script will inject widget into the container -->
<script
  src="https://app.renovationai.com/widget/your-slug.js"
  data-container="my-widget-container"
></script>`}
                      />
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium">With Custom Dimensions</h4>
                      <CodeBlock
                        id="html-custom"
                        code={`<script
  src="https://app.renovationai.com/widget/your-slug.js"
  data-width="100%"
  data-height="900"
  data-max-width="600px"
></script>`}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Customization Options */}
        <Card>
          <CardHeader>
            <CardTitle>Customization Options</CardTitle>
            <CardDescription>
              Available data attributes for the script tag embed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Attribute</th>
                    <th className="text-left py-3 px-4 font-medium">Default</th>
                    <th className="text-left py-3 px-4 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-3 px-4 font-mono text-xs">data-container</td>
                    <td className="py-3 px-4 text-muted-foreground">none</td>
                    <td className="py-3 px-4">ID or selector of container element</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4 font-mono text-xs">data-width</td>
                    <td className="py-3 px-4 text-muted-foreground">100%</td>
                    <td className="py-3 px-4">Widget width (e.g., "100%", "400px")</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 px-4 font-mono text-xs">data-height</td>
                    <td className="py-3 px-4 text-muted-foreground">800</td>
                    <td className="py-3 px-4">Widget height in pixels</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-mono text-xs">data-max-width</td>
                    <td className="py-3 px-4 text-muted-foreground">480px</td>
                    <td className="py-3 px-4">Maximum widget width</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card>
          <CardHeader>
            <CardTitle>Troubleshooting</CardTitle>
            <CardDescription>Common issues and solutions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium">Widget not appearing</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Make sure your widget is enabled in the dashboard settings</li>
                <li>Check that you're using the correct slug in the embed code</li>
                <li>Verify there are no JavaScript errors in the browser console</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Widget appears but is cut off</h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Try increasing the <code className="bg-muted px-1 rounded">data-height</code> value</li>
                <li>Check if the parent container has <code className="bg-muted px-1 rounded">overflow: hidden</code></li>
                <li>Use the iframe embed instead if the script tag has issues</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Widget doesn't match my site's colors</h4>
              <p className="text-sm text-muted-foreground">
                Customize your widget colors in the{' '}
                <Link to="/dashboard/settings/company" className="text-primary hover:underline">
                  Company Settings
                </Link>{' '}
                page. The widget automatically uses your brand colors.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Need Help */}
        <Card>
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              If you're having trouble installing the widget or have questions, we're here to help.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" asChild>
                <a href="mailto:support@renovationai.com">
                  Contact Support
                </a>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/dashboard/settings/widget">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Widget Settings
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
